import React, { useState, useEffect } from 'react';
import { useDbUser } from '../hooks/useDbUser';
import { db, Task, sql } from '../lib/db';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { calculateNextDueDate } from '../lib/dateUtils';

interface BrokenTask {
  task: Task;
  hasHistory: boolean;
  hasRegeneration: boolean;
  lastCompleted?: Date;
  isFixing?: boolean;
}

export function RegularTasksFixPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const [loading, setLoading] = useState(true);
  const [brokenTasks, setBrokenTasks] = useState<BrokenTask[]>([]);
  const [fixing, setFixing] = useState<string | null>(null);

  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadBrokenTasks();
    }
  }, [dbUserId, dbUserLoading]);


  const loadBrokenTasks = async () => {
    if (!dbUserId) return;
    
    setLoading(true);
    try {
      // Find completed regular tasks from the last 7 days
      const allTasks = await db.tasks.findByUserId(dbUserId);
      
      // First, get all active regular task templates (not completed)
      const activeRegularTemplates = allTasks.filter(t => 
        t.is_regular && t.status !== 'Complete'
      );
      
      // Create a Set of active template signatures for quick lookup
      const activeTemplateSignatures = new Set(
        activeRegularTemplates.map(t => 
          `${t.title}|${t.description || ''}|${t.frequency || 'Daily'}`
        )
      );
      
      const completedRegularTasks = allTasks.filter(t => {
        if (!t.is_regular || t.status !== 'Complete' || !t.end_date) {
          return false;
        }
        
        // Check if this task has an active template (not deleted)
        const taskSignature = `${t.title}|${t.description || ''}|${t.frequency || 'Daily'}`;
        if (!activeTemplateSignatures.has(taskSignature)) {
          // Task has been deleted, skip it
          return false;
        }
        
        return new Date(t.end_date) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      });

      // Check each task for problems
      const broken: BrokenTask[] = [];
      
      for (const task of completedRegularTasks) {
        // Check if it has completion history by querying the database directly
        const historyResult = await sql`
          SELECT * FROM regular_task_history 
          WHERE generated_task_id = ${task.id}
          AND user_id = ${dbUserId}
          AND completed_date IS NOT NULL
          LIMIT 1
        `;
        const hasHistory = historyResult.length > 0;
        
        // Only check for regeneration if the next due date has passed
        const completionDate = new Date(task.end_date!);
        completionDate.setHours(0, 0, 0, 0);
        
        // Calculate when the next task should be generated
        const frequency = task.frequency || 'Daily';
        const nextDueDate = calculateNextDueDate(completionDate, frequency, task.recurrence_day);
        
        // Only check for regeneration if the next due date has passed
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        
        if (now >= nextDueDate) {
          // The task should have been regenerated by now
          const activeTasks = await db.tasks.findByUserId(dbUserId);
          const regenerated = activeTasks.find(t => 
            t.title === task.title && 
            t.is_regular && 
            t.status !== 'Complete' &&
            t.created_at > task.end_date!
          );
          
          if (!hasHistory || !regenerated) {
            broken.push({
              task,
              hasHistory: hasHistory,
              hasRegeneration: !!regenerated,
              lastCompleted: task.end_date ? new Date(task.end_date) : undefined
            });
          }
        }
        // If the next due date hasn't passed yet, the task is not broken
      }
      
      setBrokenTasks(broken);
    } catch (error) {
      console.error('Error loading broken tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fixTask = async (brokenTask: BrokenTask) => {
    if (!dbUserId) return;
    
    setFixing(brokenTask.task.id);
    
    // Mark the task as being fixed with a slight delay for animation
    setBrokenTasks(prev => prev.map(t => 
      t.task.id === brokenTask.task.id ? { ...t, isFixing: true } : t
    ));
    
    try {
      const { task } = brokenTask;
      
      // Step 1: Create missing history if needed
      if (!brokenTask.hasHistory && task.end_date) {
        // Insert history record with the actual completion date
        const completionDate = new Date(task.end_date);
        const completionDateOnly = new Date(task.end_date);
        completionDateOnly.setHours(0, 0, 0, 0);
        
        await sql`
          INSERT INTO regular_task_history (
            task_template_id,
            generated_task_id,
            user_id,
            generated_date,
            completed_date,
            completion_time
          )
          VALUES (
            ${task.id},
            ${task.id},
            ${dbUserId},
            ${task.start_date || completionDateOnly},
            ${completionDateOnly},
            ${completionDate}
          )
          ON CONFLICT (task_template_id, generated_date) 
          DO UPDATE SET 
            completed_date = EXCLUDED.completed_date,
            completion_time = EXCLUDED.completion_time
        `;
        
        toast.success('Created missing history record');
      }
      
      // Step 2: Create new task instance if needed
      if (!brokenTask.hasRegeneration && task.end_date) {
        const completionDate = new Date(task.end_date);
        completionDate.setHours(0, 0, 0, 0);
        
        const frequency = task.frequency || 'Daily';
        const nextDueDate = calculateNextDueDate(completionDate, frequency, task.recurrence_day);
        
        const newTaskData = {
          title: task.title,
          description: task.description,
          priority: task.priority,
          is_regular: true,
          frequency: task.frequency,
          recurrence_day: task.recurrence_day,
          start_date: nextDueDate,
          deadline: nextDueDate,
          next_due_date: nextDueDate,
          status: 'To Do' as const,
          is_live: nextDueDate <= new Date(),
          user_id: dbUserId
        };
        
        const newTask = await db.tasks.create(newTaskData);
        
        // Record the generation with today's date (when we're creating it)
        await db.regularTaskHistory.recordGeneration(task.id, newTask.id, dbUserId, new Date());
        
        toast.success('Created missing task instance');
      }
      
      // Wait a moment for the success message to show
      toast.success('Task fixed successfully!');
      
      // Remove the fixed task from the list with a delay for smooth UX
      setTimeout(() => {
        setBrokenTasks(prev => prev.filter(t => t.task.id !== brokenTask.task.id));
      }, 500);
    } catch (error) {
      console.error('Error fixing task:', error);
      toast.error('Failed to fix task');
    } finally {
      setFixing(null);
    }
  };

  if (dbUserLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking regular tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Fix Broken Regular Tasks</h1>
        <p className="text-gray-600">
          These regular tasks were completed but may not regenerate properly due to missing history or task instances.
        </p>
      </div>

      {brokenTasks.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-green-900">All tasks are healthy!</h3>
          <p className="text-green-700 mt-1">No broken regular tasks found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-900">
                  Found {brokenTasks.length} task{brokenTasks.length !== 1 ? 's' : ''} that need fixing
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Click "Fix Task" to automatically repair history and regenerate instances.
                </p>
              </div>
            </div>
          </div>

          {brokenTasks.map((brokenTask) => (
            <div 
              key={brokenTask.task.id} 
              className={`bg-white border rounded-lg p-4 shadow-sm transition-all duration-500 ${
                brokenTask.isFixing ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{brokenTask.task.title}</h3>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center text-sm">
                      <span className={`flex items-center ${brokenTask.hasHistory ? 'text-green-600' : 'text-red-600'}`}>
                        {brokenTask.hasHistory ? (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 mr-1" />
                        )}
                        {brokenTask.hasHistory ? 'Has history' : 'Missing history'}
                      </span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className={`flex items-center ${brokenTask.hasRegeneration ? 'text-green-600' : 'text-red-600'}`}>
                        {brokenTask.hasRegeneration ? (
                          <CheckCircle className="w-4 h-4 mr-1" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 mr-1" />
                        )}
                        {brokenTask.hasRegeneration ? 'Has next instance' : 'Missing next instance'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Frequency: {brokenTask.task.frequency || 'Daily'} | 
                      Completed: {brokenTask.task.end_date ? new Date(brokenTask.task.end_date).toLocaleDateString() : 'Unknown'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => fixTask(brokenTask)}
                  disabled={fixing === brokenTask.task.id}
                  className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {fixing === brokenTask.task.id ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Fixing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Fix Task
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RegularTasksFixPage;